/** Confirmation modal for a pending patient workflow-status change. */

import { useEffect, useRef, useState } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";

const DIALOG_ID = ModalId.PATIENT_STATUS_CHANGE_MODAL;

/** Props for the patient-status confirmation modal. */
export interface PatientStatusChangeModalProps {
  isOpen: boolean;
  patientName: string;
  patientMrn: string;
  currentStatus: string;
  nextStatus: string;
  onClose: () => void;
  onSave: (comment: string) => Promise<void>;
}

/** Confirm a status transition and optionally capture an accompanying note. */
export function PatientStatusChangeModal({
  isOpen,
  patientName,
  patientMrn,
  currentStatus,
  nextStatus,
  onClose,
  onSave,
}: PatientStatusChangeModalProps) {
  const isModalOpenRef = useRef(false);
  const [comment, setComment] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setComment("");
      setError("");
    }
  }, [isOpen, nextStatus]);

  useEffect(() => {
    if (isOpen && !isModalOpenRef.current) {
      openModal(DIALOG_ID);
      isModalOpenRef.current = true;
    } else if (!isOpen && isModalOpenRef.current) {
      closeModal(DIALOG_ID);
      isModalOpenRef.current = false;
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (isModalOpenRef.current) closeModal(DIALOG_ID);
    },
    [],
  );

  const handleClose = () => {
    if (isSaving) return;
    closeModal(DIALOG_ID);
    isModalOpenRef.current = false;
    onClose();
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setError("");
    try {
      await onSave(comment.trim());
      closeModal(DIALOG_ID);
      isModalOpenRef.current = false;
      onClose();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not change patient status",
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      dialogId={DIALOG_ID}
      title="Change Patient Status"
      onSave={() => void handleSave()}
      onCancel={handleClose}
      onClose={handleClose}
      saveButtonText={isSaving ? "Saving…" : "Save"}
      cancelText="Cancel"
      disableSave={isSaving}
    >
      <div className="flex flex-col items-center gap-5 px-2 py-3 text-center">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full bg-tertiaryBlue-12 text-3xl font-semibold text-tertiaryBlue-5"
          aria-hidden
        >
          ?
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-primaryGray-3">
            {patientName} ({patientMrn})
          </p>
          <p className="text-base leading-6 text-primaryGray-6">
            Change patient status from{" "}
            <strong className="text-primaryGray-3">{currentStatus}</strong> to{" "}
            <strong className="text-primaryGray-3">{nextStatus}</strong>?
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 text-left">
          <label
            htmlFor="patient-status-comment"
            className="text-center text-sm font-semibold text-primaryGray-3"
          >
            Comment{" "}
            <span className="font-normal text-primaryGray-8">(optional)</span>
          </label>
          <textarea
            id="patient-status-comment"
            value={comment}
            placeholder="Add a note (optional)"
            className="min-h-[6.5rem] w-full resize-y rounded-md border border-primaryGray-14 bg-white px-3 py-2 text-sm leading-5 text-primaryGray-1 outline-none placeholder:text-primaryGray-10 focus:border-primaryGray-2"
            onChange={(event) => setComment(event.target.value)}
          />
        </div>
        {error ? (
          <p className="w-full text-left text-xs text-statusRed-text" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
